import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <h1 className="font-heading text-3xl text-foreground-900 mb-4">
          文章未找到
        </h1>
        <Link
          to="/"
          className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const relatedArticles = articles
    .filter(
      (a) => a.categorySlug === article.categorySlug && a.id !== article.id
    )
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '六合宝典',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://liuhebaodian.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <title>{article.title} | 六合宝典</title>
      <meta name="description" content={article.excerpt} />
      <meta
        name="keywords"
        content={`六合宝典,${article.title},${article.tags.join(',')}`}
      />
      <link
        rel="canonical"
        href={`https://liuhebaodian.com/article/${article.slug}`}
      />
      <meta property="og:title" content={`${article.title} | 六合宝典`} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta
        property="og:url"
        content={`https://liuhebaodian.com/article/${article.slug}`}
      />
      <meta
        property="og:image"
        content={article.imageUrl}
      />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${article.title} | 六合宝典`} />
      <meta name="twitter:description" content={article.excerpt} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(articleSchema),
        }}
      />

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            {
              label: article.category,
              href: `/category/${article.categorySlug}`,
            },
            { label: article.title },
          ]}
        />

        <article className="mt-6">
          {/* Article Header */}
          <div className="mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-xs font-medium text-primary-700 mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-3 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-md bg-secondary-100 text-xs text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Featured Image */}
          <div className="rounded-2xl overflow-hidden mb-10">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={`${article.title} - 六合宝典`}
              className="w-full h-auto object-cover"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose-custom"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="mt-16 pt-10 border-t border-background-200/70">
              <h2 className="font-heading text-2xl text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-50 rounded-xl p-4 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="h-32 rounded-lg overflow-hidden mb-3">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        title={`${rel.title} - 六合宝典`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h3 className="font-heading text-sm text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors mb-1">
                      {rel.title}
                    </h3>
                    <span className="text-xs text-foreground-400">
                      {rel.publishDate}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    </>
  );
}